export interface OBLOResult {
  index: number
  userName: string
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  accessLevel: string
  biz_channel: {
    id: number
    name: string
  }
}

/**
 * Per-LO configuration entry in lo_map.
 * LO-specific values are merged into the pricing request before being sent to the engine.
 * Precedence (highest wins): Request params > LO defaults > Global defaults
 */
export interface LOMapEntry {
  /** Optimal Blue business channel ID */
  biz_channel?: string
  /** Optimal Blue originator ID */
  originator_id?: string
  /** Loan Sifter / Lender Price user ID */
  user_id?: string
  /** Loan Sifter / Lender Price company ID */
  company_id?: string
  /** MorTech customer ID */
  customer_id?: string
  /** MorTech email */
  email?: string
  /** BB request field defaults (e.g., lock_period, waive_escrows) */
  defaults?: Record<string, any>
  /** Engine-specific values with dot notation for nested fields (e.g., "criteria.channel") */
  raw_defaults?: Record<string, any>
}

export type LOMap = Record<string, LOMapEntry>

export interface EscrowMonths {
  /** Months of homeowners-insurance premium prepaid at closing */
  insurance?: number
  /** Months of insurance held in reserves (multiplies the insurance amount, not taxes) */
  insurance_reserve?: number
  /** Months of property tax held in reserves */
  tax_reserve?: number
}

export interface RateflowSettings {
  api_mode: PricingEngineType
  api_config: {
    // Polly custom fields stored here
    custom_values?: PollyCustomField[]
    prepaid_interest_days?: number
    default_fees_in?: boolean
    fee_engine?: string
    [key: string]: any
  }
  lo_map?: LOMap
  cache_ttl?: number
  target_price?: number
  waive_escrows?: boolean
  /**
   * Per-brand escrow months shown in the loan-summary fee breakdown — both the
   * fees modal and the flyer PDFs. Separate values per loan purpose; omitted
   * fields fall back to baked-in defaults. Does not affect rate/live pricing.
   */
  escrow_months?: {
    purchase?: EscrowMonths
    refinance?: EscrowMonths
  }
  disable?: boolean
  include_affordable_products?: boolean
  /** Global BB request field defaults applied to all LOs (e.g., lock_period) */
  defaults?: Record<string, any>
  /**
   * Engine-specific raw defaults.
   * - loanInformation.customFields: Optimal Blue custom fields
   * - Other keys: flat key-value pairs with dot notation for nested fields
   */
  raw_defaults?: {
    loanInformation?: {
      customFields?: CustomFieldValue[]
    }
    [key: string]: any
  }
}

export type PricingEngineType =
  | "optimal_blue"
  | "loan_sifter"
  | "polly"
  | "mortech"
  | "lender_price"
  | "loanpass"
  | "mortgage_bot"
  | "epps"

// Optimal Blue custom field format
export interface CustomFieldValue {
  customFieldInputName: string
  customFieldValue: string | number
}

// Polly custom field format (simpler)
export interface PollyCustomField {
  name: string
  value: string
}

export interface CustomFieldListValue {
  customFieldListDescription: string
  customFieldValue: number
  displayInList: boolean
}

export interface CustomFieldDefinition {
  customFieldInputName: string
  customFieldDescription: string
  typeOfField: "List" | "Text" | "Number"
  columnName: string
  isRequired: boolean
  defaultValue?: string | number
  customListValues?: CustomFieldListValue[]
  submissionForm: string
}

export type CustomFieldsResponse = Record<string, CustomFieldDefinition>

export interface BrandMember {
  loid: string
  name: string
  first_name: string
  last_name: string
  email: string
}

export interface PendingLOMapping {
  obLO: OBLOResult
  memberId: string | null
}

export const PRICING_ENGINE_LABELS: Record<PricingEngineType, string> = {
  optimal_blue: "Optimal Blue",
  loan_sifter: "Loan Sifter",
  polly: "Polly",
  mortech: "Mortech",
  lender_price: "Lender Price",
  loanpass: "LoanPass",
  mortgage_bot: "MortgageBot",
  // ICE PPE was formerly known as Encompass Product and Pricing Service (EPPS)
  epps: "ICE PPE"
}

export const ENGINES_WITH_LO_MAP: PricingEngineType[] = [
  "optimal_blue",
  "loan_sifter",
  "polly",
  "mortech",
  "lender_price",
  "loanpass",
  "mortgage_bot",
  "epps"
]

export const ENGINES_WITH_CUSTOM_FIELDS: PricingEngineType[] = [
  "optimal_blue",
  "polly"
]
